import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getIsWebCrawler } from "@insite/client-framework/Components/ContentItemStore";
import setView from "@insite/client-framework/Store/Pages/ProductList/Handlers/SetView";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import { ProductListViewType } from "@insite/client-framework/Store/Pages/ProductList/ProductListState";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import CardContainerMultiColumn, {
    CardContainerMultiColumnStyles,
} from "@insite/content-library/Components/CardContainerMultiColumn";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
import { ProductListPageDataContext } from "@insite/content-library/Pages/ProductListPage";
import ProductListProductContext from "@insite/content-library/Widgets/ProductList/ProductListProductContext";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Hidden from "@insite/mobius/Hidden";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductListProductCard from "@store/Overrides/Widgets/ProductList/ProductListProductCard";
import { YMMDProductsData } from "@store/Pages/ProductListYmmdPage";
import { useProductDataView } from "@store/Store/Components/RsswYMMD/Hooks/useYMMD";
import { ymmdFitmentsSelector } from "@store/Store/Components/RsswYMMD/RsswYMMDSelector";
import StoreApplicationState from "@store/Store/StoreApplicationState";
import { COLORS, TYPOGRAPHY } from "@store/theme/common/design";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import ProductListProductGridCardYmmd from "@store/Widgets/ProductList/ProductListProductGridCardYmmd";
import ProductListFitments from "@store/Widgets/Ymmd/ProductListFitments";
import React, { FC, ReactNode, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showImage = "showImage",
    showCompare = "showCompare",
    showBrand = "showBrand",
    showTitle = "showTitle",
    showPartNumbers = "showPartNumbers",
    showAvailability = "showAvailability",
    showAttributes = "showAttributes",
    showPrice = "showPrice",
    showAddToList = "showAddToList",
    productListDefaultView = "productListDefaultView",
    showMiniPhoto = "showMiniPhoto",
    showDocuments = "showDocuments",
    showEta = "showEta",
    showIndustryNumber = "showIndustryNumber",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showImage]: boolean;
        [fields.showCompare]: boolean;
        [fields.showTitle]: boolean;
        [fields.showBrand]: boolean;
        [fields.showPartNumbers]: boolean;
        [fields.showAvailability]: boolean;
        [fields.showAttributes]: boolean;
        [fields.showPrice]: boolean;
        [fields.showAddToList]: boolean;
        [fields.productListDefaultView]: ProductListViewType;
        [fields.showMiniPhoto]: boolean;
        [fields.showDocuments]: boolean;
        [fields.showEta]: boolean;
        [fields.showIndustryNumber]: boolean;
    };
}

interface SectionProps {
    name: string;
    products: ProductModel[];
}

const mapStateToProps = (state: StoreApplicationState, ownProps: OwnProps) => {
    const {
        pages: {
            productList: { isSearchPage },
        },
    } = state;
    return {
        isLoadingYMMD: state.store.components.rsswYMMD.isLoading,
        isLoading: state.pages.productList.isLoading,
        productsDataView: getProductListDataView(state),
        view: state.pages.productList.view || ownProps.fields.productListDefaultView,
        isSearchPage,
        fitments: ymmdFitmentsSelector(state),
    };
};

const mapDispatchToProps = {
    setView,
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductListCardListStyles {
    wrapper?: InjectableCss;
    categoryFiltersWrapper?: InjectableCss;
    productsWrapper?: InjectableCss;
    cardList?: CardListStyles;
    noProductsText?: TypographyPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    cardContainerStyles?: CardContainerStyles;
    cardContainerMultiColumnStyles?: CardContainerMultiColumnStyles;
    sectionTitle?: TypographyPresentationProps;
    categoryFilterLabel?: TypographyPresentationProps;
    changeCategoryFilter?: ButtonPresentationProps;
}

export const listStyles: ProductListCardListStyles = {
    categoryFiltersWrapper: {
        css: css`
            margin-top: 10px;
            margin-bottom: 50px;
        `,
    },
    productsWrapper: {
        css: css`
            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: ["50px", null, "80px", null, null], properties: ["margin-bottom"] }],
                    mediaOptions.min,
                )}
        `,
    },
    cardList: {
        gridContainer: {
            gap: 20,
        },
    },
    centeringWrapper: {
        css: css`
            width: 100%;
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noProductsText: {
        variant: "h4",
        css: css`
            display: block;
            margin: auto;
        `,
    },
    cardContainerStyles: {
        cardDivider: {
            css: css`
                width: 100%;
            `,
        },
        gridItem: {
            width: 12,
            css: css`
                border-bottom: 1px solid ${getColor("common.border")};
            `,
        },
    },
    cardContainerMultiColumnStyles: {
        gridItem: {
            width: [12, 12, 4, 3, 3],
        },
        cardDivider: {
            css: css`
                padding: 15px;
                border: 1px solid ${getColor("common.border")};
                width: 100%;
                height: 100%;
            `,
        },
    },
    sectionTitle: {
        variant: "h2",
        css: css`
            padding-bottom: 10px;
            margin-top: 70px;
            font-size: 26px !important;
        `,
    },
    categoryFilterLabel: {
        size: TYPOGRAPHY.size.base,
        color: COLORS.secondary.base,
        css: css`
            display: block;
            margin-bottom: 10px;
        `,
    },
    changeCategoryFilter: {
        css: css`
            margin-right: 18px;
            margin-bottom: 18px;
        `,
    },
};

const styles = listStyles;

const ProductListCardList: FC<Props> = ({
    isLoading,
    isLoadingYMMD,
    productsDataView,
    view,
    fields,
    setView,
    fitments,
}) => {
    const productYmmdDataView = useProductDataView();
    const [sortedSections, setSortedSections] = useState<any>([]);
    const [sortedProductsBySections, setSortedProductsBySections] = useState<any>([]);
    const [filterCategory, setFilterCategory] = useState("AllCategories");

    useEffect(() => {
        if (productYmmdDataView.productsIds?.length > 0) {
            // Sort the YMMD response into multiple arrays based on section name
            const sortedBySectionApiResults: any = productYmmdDataView.productsIds.reduce(
                (h, obj) => Object.assign(h, { [obj.section]: (h[obj.section] || []).concat(obj) }),
                {} as { [key: string]: any },
            );
            setSortedSections(sortedBySectionApiResults);
        } else {
            setSortedSections([]);
        }
    }, [productYmmdDataView]);

    const filterCategoryHandler = (filter: string) => {
        setFilterCategory(filter);
    };

    useEffect(() => {
        setFilterCategory("AllCategories");
        if (
            productYmmdDataView.productsIds?.length > 0 &&
            productsDataView.value !== undefined &&
            productsDataView.value.length > 0
        ) {
            const tempSortedSections = [];
            for (const sortedProducts in sortedSections) {
                tempSortedSections.push({
                    name: sortedProducts,
                    products: sortedSections[sortedProducts]
                        .map((product: YMMDProductsData) => {
                            const tempProducts = {
                                fit: product.fit,
                                ...productsDataView.value?.find(item => product.productId === item.id),
                            };
                            return tempProducts;
                        })
                        .filter((product: YMMDProductsData) => Object.keys(product).length > 1),
                });
            }
            setSortedProductsBySections(tempSortedSections);
        } else {
            setSortedProductsBySections([]);
        }
    }, [productsDataView.value]);

    if (
        (productYmmdDataView?.productsIds?.length > 0 && sortedProductsBySections.length <= 0) ||
        isLoadingYMMD ||
        isLoading
    ) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} data-test-selector="productListCardListSpinner" />
            </StyledWrapper>
        );
    }

    if (productYmmdDataView?.productsIds?.length <= 0 && !fitments.length && !isLoadingYMMD && !isLoading) {
        return <Typography>{translate("No results found. Please try again.")}</Typography>;
    }

    const sections = sortedProductsBySections;

    const renderProducts = (children: ReactNode) => {
        if (fitments.length && sections.length === 0) {
            return (
                <CardList extendedStyles={styles.cardList}>
                    <ProductListFitments />
                </CardList>
            );
        }

        return sections.map((section: SectionProps) => {
            if (filterCategory !== "AllCategories" && filterCategory !== section.name) {
                return null;
            }

            const hasFitments = fitments?.filter(fit => fit.section === section.name).length;

            return (
                <StyledWrapper {...styles.productsWrapper} key={section.name}>
                    {(section.products.length > 0 || hasFitments > 0) && (
                        <Typography {...styles.sectionTitle}>{translate(section.name)}</Typography>
                    )}
                    <CardList extendedStyles={styles.cardList}>
                        {hasFitments > 0 && <ProductListFitments />}
                        {section.products.map((product, idx) => {
                            return (
                                <ProductListProductContext product={product} idx={idx} key={product.id}>
                                    {children}
                                </ProductListProductContext>
                            );
                        })}
                    </CardList>
                </StyledWrapper>
            );
        });
    };

    const overriddenProps = { ...fields };
    if (getIsWebCrawler()) {
        overriddenProps.showAvailability = false;
    }

    if (isLoadingYMMD) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    const renderCategoryFilters = () => {
        const sortedSectionsKeys = Object.keys(sortedSections);
        const categoryFilters = ["AllCategories", ...sortedSectionsKeys];

        return (
            <StyledWrapper {...styles.categoryFiltersWrapper}>
                <Typography {...styles.categoryFilterLabel}>{translate("Filter by Category")} : </Typography>
                {categoryFilters.map(filter => {
                    return (
                        <Button
                            key={filter}
                            variant={filter === filterCategory ? "primary" : "secondary"}
                            {...styles.changeCategoryFilter}
                            onClick={() => filterCategoryHandler(filter)}
                        >
                            {translate(filter)}
                        </Button>
                    );
                })}
            </StyledWrapper>
        );
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            {renderCategoryFilters()}
            <ProductListPageDataContext.Consumer>
                {({ ref }) => {
                    return ref ? <span ref={ref} tabIndex={-1} /> : undefined;
                }}
            </ProductListPageDataContext.Consumer>
            <LoadingOverlay loading={isLoading}>
                <Hidden below="md">
                    {renderProducts(
                        <CardContainerMultiColumn extendedStyles={styles.cardContainerMultiColumnStyles}>
                            <ProductListProductGridCardYmmd {...overriddenProps} />
                        </CardContainerMultiColumn>,
                    )}
                </Hidden>
                <Hidden above="sm">
                    {renderProducts(
                        <CardContainer extendedStyles={styles.cardContainerStyles}>
                            <ProductListProductCard {...overriddenProps} />
                        </CardContainer>,
                    )}
                </Hidden>
            </LoadingOverlay>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListCardList),
    definition: {
        group: "Common",
        displayName: "Card List Ymmd",
        fieldDefinitions: [
            {
                name: fields.showImage,
                displayName: "Show Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showCompare,
                displayName: "Show Compare",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.showBrand,
                displayName: "Show Brand",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showTitle,
                displayName: "Show Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showMiniPhoto,
                displayName: "Show Mini Photo",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showPartNumbers,
                displayName: "Show Part Numbers",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showAvailability,
                displayName: "Show Availability",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.showAttributes,
                displayName: "Show Attributes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 7,
            },
            {
                name: fields.showPrice,
                displayName: "Show Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 8,
            },
            {
                name: fields.showAddToList,
                displayName: "Show Add To List",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 9,
            },
            {
                name: fields.showDocuments,
                displayName: "Show Documents",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 10,
            },
            {
                name: fields.showIndustryNumber,
                displayName: "Show Industry Number",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 12,
            },
            {
                name: fields.productListDefaultView,
                editorTemplate: "RadioButtonsField",
                defaultValue: "List",
                fieldType: "General",
                sortOrder: 11,
                options: [
                    {
                        displayName: "List",
                        value: "List",
                    },
                    {
                        displayName: "Grid",
                        value: "Grid",
                    },
                    {
                        displayName: "Table",
                        value: "Table",
                    },
                ],
            },
            {
                name: fields.showEta,
                displayName: "Show Product ETA",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 12,
            },
        ],
    },
};

export default widgetModule;
